import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import './SMSDetailsPage.css';

const SMSDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const phoneNumber = searchParams.get('phone');
  
  const [smsData, setSmsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!phoneNumber) {
      setError('Phone number is required. Use ?phone=+1234567890');
      setLoading(false);
      return;
    }

    const fetchSMSDetails = async () => {
      try {
        setLoading(true);
        
        const response = await api.get(
          `/inbound_calls/sms/details/${phoneNumber}/`
        );

        setSmsData(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching SMS details:', err);
        setError(
          err.response?.data?.detail || 
          'Failed to load SMS details. Please check the phone number.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSMSDetails();
  }, [phoneNumber]);

  if (loading) {
    return (
      <div className="sms-details-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading SMS details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sms-details-container">
        <div className="error-card">
          <h2>Error</h2>
          <p>{error}</p>
          <p className="hint">Try accessing with: ?phone=%2B1978445564</p>
        </div>
      </div>
    );
  }

  if (!smsData) {
    return (
      <div className="sms-details-container">
        <div className="error-card">
          <h2>No data</h2>
          <p>Could not retrieve SMS details</p>
        </div>
      </div>
    );
  }

  const metadata = smsData.metadata || {};
  const messages = smsData.messages || [];

  return (
    <div className="sms-details-container">
      <div className="sms-details-wrapper">
        {/* Header */}
        <div className="sms-header">
          <h1>SMS Conversation Details</h1>
          <p className="phone-heading">{metadata.phone_number}</p>
        </div>

        {/* Metadata Section */}
        <div className="metadata-section">
          <h2>Conversation Info</h2>
          <div className="metadata-grid">
            <div className="metadata-card">
              <span className="label">👤 Name</span>
              <span className="value">{metadata.name || '—'}</span>
            </div>
            <div className="metadata-card">
              <span className="label">📍 Address</span>
              <span className="value">{metadata.address || '—'}</span>
            </div>
            <div className="metadata-card">
              <span className="label">✓ Status</span>
              <span className={`value status-badge ${metadata.status}`}>
                {metadata.status || '—'}
              </span>
            </div>
            <div className="metadata-card">
              <span className="label">📱 From</span>
              <span className="value phone">{metadata.from_number || '—'}</span>
            </div>
            <div className="metadata-card">
              <span className="label">📞 To</span>
              <span className="value phone">{metadata.to_number || '—'}</span>
            </div>
            <div className="metadata-card">
              <span className="label">🕐 Last Active</span>
              <span className="value time">
                {metadata.last_received_at
                  ? new Date(metadata.last_received_at).toLocaleDateString()
                  : '—'}
              </span>
            </div>
            <div className="metadata-card">
              <span className="label">💬 Total SMS</span>
              <span className="value number">{metadata.total_sms_records || 0}</span>
            </div>
            <div className="metadata-card">
              <span className="label">📧 Messages</span>
              <span className="value number">{metadata.total_messages || 0}</span>
            </div>
          </div>
        </div>

        {/* Conversation Section */}
        <div className="conversation-section">
          <div className="conversation-header">
            <h2>💬 Conversation Thread</h2>
            <span className="message-count">{messages.length} messages</span>
          </div>
          <div className="messages-container">
            {messages.length > 0 ? (
              messages.map((msg, index) => (
                <div 
                  key={index} 
                  className={`message-bubble ${msg.speaker.toLowerCase() === 'ai' ? 'ai-message' : 'customer-message'}`}
                >
                  <div className="message-header">
                    <span className="message-speaker">
                      {msg.speaker.toLowerCase() === 'ai' ? '🤖' : '👤'} {msg.speaker}
                    </span>
                    <span className="message-index">#{index + 1}</span>
                  </div>
                  <div className="message-text">
                    {msg.text}
                  </div>
                </div>
              ))
            ) : (
              <p className="no-messages">No messages available</p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default SMSDetailsPage;
